import type { Ad } from "@/app/components/ad-card";
import type { Category } from "@/app/api/auth-client";
import { buildCategoryPathLabel, isOpaqueCatalogCategoryId } from "@/app/utils/category-tree";

/** Canonical GCS object key for ad images (matches Bonamassa signed-URL rules). */
export function normalizeAdImageStoragePath(s: string): string {
  const t = typeof s === "string" ? s.trim() : "";
  if (t.length >= 4 && /^ads\//i.test(t.slice(0, 4))) {
    const rest = t.slice(4).replace(/^\//, "").trim();
    return rest ? `ads/${rest}` : "ads/";
  }
  return t;
}

/** True if the string is a storage path (opaque key) that needs signed URL resolution. */
export function isStoragePath(s: string): boolean {
  return typeof s === "string" && /^ads\//i.test(s.trim());
}

/**
 * Gets the primary image URL or storage path from an Ad object.
 * Prefers user_images[0] (storage path), then image, imageUrl, images[0].
 */
export function getAdImage(ad: Ad): string {
  if (ad.user_images && ad.user_images.length > 0) return ad.user_images[0];
  return ad.image || ad.imageUrl || ad.images?.[0] || "";
}

/**
 * Gets all image URLs/paths from an Ad object. Storage paths (user_images) come first.
 */
export function getAdImages(ad: Ad): string[] {
  if (ad.user_images && ad.user_images.length > 0) return ad.user_images;
  if (ad.images && ad.images.length > 0) return ad.images;
  const primary = getAdImage(ad);
  return primary ? [primary] : [];
}

/**
 * When API omitted category_tree or echoed raw category_id, resolve "Parent > Child" from catalog.
 */
export function enrichAdCategoryFromCatalog(ad: Ad, catalog: Category[] | null | undefined): Ad {
  if (!catalog?.length || !ad.category_id?.trim()) return ad;
  const id = ad.category_id.trim();
  const tree = ad.category_tree?.trim() ?? "";
  // Already have a human path different from the opaque id
  if (tree && tree !== id) return ad;
  if (tree && tree === id && !isOpaqueCatalogCategoryId(id)) return ad;
  const path = buildCategoryPathLabel(catalog, id);
  if (!path) return ad;
  return { ...ad, category_tree: path, category: path };
}

/** Chip label for ad cards: never show raw cat_* id when we can avoid it. */
export function adCategoryBadgeLabel(ad: Ad): string {
  const tree = ad.category_tree?.trim() ?? "";
  const id = ad.category_id?.trim() ?? "";
  const cat = ad.category?.trim() ?? "";
  if (tree && (!id || tree !== id)) return tree;
  if (cat && cat !== id && !isOpaqueCatalogCategoryId(cat)) return cat;
  if (cat && cat.includes(">")) return cat;
  if (id && !isOpaqueCatalogCategoryId(id)) return id;
  return "Outros";
}

/** Uma linha para UI (detalhe, Meus Anúncios): árvore "A > B" → "A · B › …"; senão `category` legível (sem cat_*). */
export function formatAdCategoryDisplayLine(
  ad: Pick<Ad, "category_tree" | "category" | "category_id">
): string | null {
  const tree = (ad.category_tree ?? "").trim();
  if (tree) {
    const parts = tree.split(/\s*>\s*/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) return `${parts[0]} · ${parts.slice(1).join(" › ")}`;
    if (parts.length === 1) return parts[0];
  }
  const cat = (ad.category ?? "").trim();
  if (cat && !/^cat_[a-zA-Z0-9_-]+$/i.test(cat)) return cat;
  return null;
}
